import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, CircularProgress } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { ApiError, DetailView } from '@pro4-erp/core-web';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { ClientWorkOrderDetails } from '../../types';
import { getClientWorkOrderById } from '../../api/getClientWorkOrdersById';
import Form from '../../components/Form';
import { updateWorkOrderProposal } from '../../api/updateWorkOrderProposal';
import { getMessageFromError } from '@/core';
import { useDetailPageStore } from './useDetailPageStore';

const DetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>() as { id: string };

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const detailPageStore = useDetailPageStore();

  const query = useQuery<ClientWorkOrderDetails, ApiError>(
    ['client-work-order-id', id],
    () => getClientWorkOrderById(id),
    { refetchOnWindowFocus: false }
  );

  const { mutate } = useMutation({
    mutationFn: updateWorkOrderProposal,
    onSuccess: () => {
      enqueueSnackbar(
        t('Successfully saved changes.', { defaultValue: 'Successfully saved changes.' }),
        { variant: 'success' }
      );
      queryClient.invalidateQueries(['client-work-order-id', id]);
      navigate('/client-work-orders');
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  useEffect(() => {
    return () => {
      detailPageStore.reset();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!query.data) {
      return;
    }

    detailPageStore.setForm({
      title: query.data.title || '',
      address: query.data.address || '',
      postOffice: query.data.postOffice || { id: '' },
      employees: query.data.employees || 0,
      contacts: query.data.contacts || [],
    });
    detailPageStore.setIsLoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data]);

  if (query.isLoading || detailPageStore.isLoading) {
    return (
      <Box>
        <Box
          sx={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={100} />
        </Box>
      </Box>
    );
  }

  if (query.isError) {
    return <p>{query.error.message}</p>;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <DetailView
        childrenSx={{ padding: '2rem' }}
        title={
          <DetailView.Title>{`${t('WO', { defaultValue: 'WO' })}: ${
            query.data.title || 'N/A'
          }`}</DetailView.Title>
        }
        onGoBack={() => navigate('/client-work-orders')}
        tabs={[{ id: 'details', title: t('Details', { defaultValue: 'Details' }) }]}
        defaultTab="details"
      >
        {({ TabContent }) => (
          <TabContent tab="details">
            <Form
              onSubmit={(data) => mutate({ id: query.data.id, data })}
              initialState={detailPageStore.form}
              hasFormChanged={(form) => {
                if (
                  form.title === query.data.title &&
                  form.address === query.data.address &&
                  form.postOffice === query.data.postOffice &&
                  form.employees === query.data.employees &&
                  form.contacts === query.data.contacts
                ) {
                  return false;
                }

                return true;
              }}
              onUnmount={(form) => detailPageStore.setForm({ ...form })}
              wo_id={id}
              refetchWo={() => query.refetch()}
              isFormEditable
            />
          </TabContent>
        )}
      </DetailView>
    </Box>
  );
};

export default DetailPage;
